import React, { useState } from "react";
import useSWR, { mutate } from "swr";
import { api, STATUS_TO_PILL } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, RefreshCw, AlertTriangle } from "lucide-react";

const fetcher = (u) => api.get(u).then(r=>r.data);

// ---------- Receive Payment Modal ----------
export function PaymentModal({ invoice, onClose }) {
  const [f, setF] = useState({
    amount: invoice.balance || 0, mode: "BANK_TRANSFER",
    date: new Date().toISOString().slice(0,10), reference: "", notes: "",
  });
  const { data: payments } = useSWR(`/invoices/${invoice.invoice_id}/payments`, fetcher);

  const submit = async () => {
    try {
      const r = await api.post(`/invoices/${invoice.invoice_id}/payments`, f);
      toast.success(`Payment recorded — Receipt ${r.data.receipt_no}`);
      mutate("/invoices"); mutate(`/invoices/${invoice.invoice_id}/payments`);
      onClose(r.data);
    } catch(e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={()=>onClose(null)}>
      <div className="bg-white rounded-xl w-full max-w-2xl p-5 max-h-[90vh] overflow-y-auto" onClick={e=>e.stopPropagation()} data-testid="payment-modal">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="font-bold text-slate-900">Receive Payment</h3>
            <div className="text-xs text-slate-500">Invoice <span className="font-mono">{invoice.invoice_no}</span> · Total ₹{(invoice.total||0).toLocaleString()} · Balance <span className="font-semibold text-red-600">₹{(invoice.balance||0).toLocaleString()}</span></div>
          </div>
          <button onClick={()=>onClose(null)} className="text-slate-400">✕</button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-[11px] uppercase font-semibold text-slate-500">Amount *</label><input data-testid="pmt-amount" type="number" step="0.01" value={f.amount} onChange={e=>setF({...f, amount: Number(e.target.value)})} className="pi"/></div>
          <div><label className="text-[11px] uppercase font-semibold text-slate-500">Mode *</label><select data-testid="pmt-mode" value={f.mode} onChange={e=>setF({...f, mode:e.target.value})} className="pi">{["UPI","CHEQUE","BANK_TRANSFER","CASH","CREDIT_NOTE","OTHER"].map(m=><option key={m}>{m}</option>)}</select></div>
          <div><label className="text-[11px] uppercase font-semibold text-slate-500">Date</label><input type="date" value={f.date} onChange={e=>setF({...f, date:e.target.value})} className="pi"/></div>
          <div><label className="text-[11px] uppercase font-semibold text-slate-500">Reference (UTR/Cheque No)</label><input data-testid="pmt-reference" value={f.reference} onChange={e=>setF({...f, reference:e.target.value})} className="pi"/></div>
          <div className="col-span-2"><label className="text-[11px] uppercase font-semibold text-slate-500">Notes</label><textarea value={f.notes} onChange={e=>setF({...f, notes:e.target.value})} className="pi" rows={2}/></div>
        </div>

        {payments?.length > 0 && (
          <div className="mt-4">
            <div className="text-xs font-semibold uppercase text-slate-600 mb-2">Previous payments</div>
            <table className="data-table">
              <thead><tr><th>Receipt</th><th>Date</th><th>Mode</th><th>Amount</th><th>Reference</th></tr></thead>
              <tbody>{payments.map(p=>(<tr key={p.payment_id}><td className="font-mono text-xs">{p.receipt_no}</td><td className="text-xs">{p.date}</td><td>{p.mode}</td><td className="font-mono">₹{p.amount.toLocaleString()}</td><td className="text-xs">{p.reference}</td></tr>))}</tbody>
            </table>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <button onClick={()=>onClose(null)} className="text-sm px-3 py-1.5 rounded border border-slate-200">Cancel</button>
          <button data-testid="btn-submit-payment" onClick={submit} className="text-sm px-4 py-1.5 rounded bg-emerald-600 text-white">Record ₹{f.amount.toLocaleString()}</button>
        </div>
        <style>{`.pi{width:100%;font-size:.85rem;border:1px solid #E2E8F0;border-radius:6px;padding:.4rem .5rem;background:#fff}`}</style>
      </div>
    </div>
  );
}

// ---------- Aged Receivables ----------
export function AgedReceivablesPage() {
  const { data } = useSWR("/reports/aged-receivables", fetcher);
  const t = data?.totals || {}; const rows = data?.rows || [];
  return (
    <div className="space-y-4" data-testid="aged-receivables-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Aged Receivables</h1>
        <p className="text-sm text-slate-500">Who owes you money and how long they've been sitting on it · as of {data?.as_of?.slice(0,10)}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <div className="kpi-card"><div className="kpi-label">Total Due</div><div className="kpi-value">₹{((t.total_due||0)/100000).toFixed(2)}L</div></div>
        <div className="kpi-card status-good"><div className="kpi-label">Not Due Yet</div><div className="kpi-value">₹{((t.current||0)/100000).toFixed(2)}L</div></div>
        <div className="kpi-card"><div className="kpi-label">0-30 days</div><div className="kpi-value">₹{((t.d0_30||0)/100000).toFixed(2)}L</div></div>
        <div className="kpi-card status-warn"><div className="kpi-label">31-60 days</div><div className="kpi-value">₹{((t.d31_60||0)/100000).toFixed(2)}L</div></div>
        <div className="kpi-card status-warn"><div className="kpi-label">61-90 days</div><div className="kpi-value">₹{((t.d61_90||0)/100000).toFixed(2)}L</div></div>
        <div className="kpi-card status-danger"><div className="kpi-label">90+ days</div><div className="kpi-value">₹{((t.d90_plus||0)/100000).toFixed(2)}L</div></div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-auto">
        <table className="data-table" data-testid="aged-table">
          <thead><tr><th>Customer</th><th>Phone</th><th>Invoices</th><th>Not Due</th><th>0-30</th><th>31-60</th><th>61-90</th><th>90+</th><th>Total Due</th><th>Oldest</th></tr></thead>
          <tbody>
            {rows.length===0 && <tr><td colSpan={10} className="text-center text-slate-400 py-6">No pending receivables 🎉</td></tr>}
            {rows.map(r => (
              <tr key={r.customer_id} data-testid={`aged-row-${r.customer_id}`}>
                <td className="font-semibold">{r.customer_name}</td>
                <td className="text-xs">{r.phone}</td>
                <td className="text-xs">{r.invoice_count}</td>
                <td className="font-mono">₹{r.current?.toLocaleString()}</td>
                <td className="font-mono">₹{r.d0_30?.toLocaleString()}</td>
                <td className="font-mono text-amber-700">₹{r.d31_60?.toLocaleString()}</td>
                <td className="font-mono text-amber-700">₹{r.d61_90?.toLocaleString()}</td>
                <td className="font-mono text-red-600 font-semibold">₹{r.d90_plus?.toLocaleString()}</td>
                <td className="font-mono font-bold">₹{r.total_due?.toLocaleString()}</td>
                <td className={r.oldest_days > 60 ? "text-red-600 font-semibold" : ""}>{r.oldest_days}d</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- Zoho Sync Settings ----------
export function ZohoSyncPage() {
  const { data, mutate: reload } = useSWR("/zoho/settings", fetcher);
  const [f, setF] = useState({});
  const [syncing, setSyncing] = useState(false);
  React.useEffect(() => {
    if (data) setF(prev => ({
      ...prev,
      region: data.region || "in",
      sync_invoices: data.sync_invoices ?? true,
      sync_purchase_orders: data.sync_purchase_orders ?? true,
      organization_id: data.organization_id || "",
    }));
  }, [data]);

  const save = async () => {
    try {
      await api.put("/zoho/settings", f);
      toast.success("Saved"); reload();
    } catch(e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const syncNow = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/zoho/sync-now");
      if (r.data.ok) toast.success(`Synced ${r.data.invoices||0} invoices, ${r.data.purchase_orders||0} POs`);
      else toast.error(r.data.reason || "Sync failed");
      reload();
    } catch(e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSyncing(false); }
  };

  return (
    <div className="space-y-4 max-w-3xl" data-testid="zoho-sync-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Zoho Books Sync</h1>
        <p className="text-sm text-slate-500">Auto-pull invoices and purchase orders from Zoho Books every hour.</p>
      </div>

      <div className={`p-4 rounded-xl border ${data?.configured ? "bg-emerald-50 border-emerald-200":"bg-amber-50 border-amber-200"} flex items-start gap-3`}>
        {data?.configured ? <CheckCircle2 className="text-emerald-600 shrink-0" size={20}/> : <AlertTriangle className="text-amber-600 shrink-0" size={20}/>}
        <div className="flex-1">
          <div className="font-semibold text-slate-900">{data?.configured ? "Zoho connected" : "Not configured yet"}</div>
          <div className="text-xs text-slate-600 mt-1">
            {data?.configured
              ? <>Last sync: {data?.last_sync_at ? new Date(data.last_sync_at).toLocaleString() : "never"} · Result: {JSON.stringify(data?.last_sync_result || {})}</>
              : <>Get a self-client OAuth refresh token from Zoho: <a href="https://api-console.zoho.in/" target="_blank" rel="noopener noreferrer" className="text-sky-600 underline">api-console.zoho.in</a> · scopes <span className="font-mono">ZohoBooks.invoices.READ ZohoBooks.purchaseorders.READ</span> · then paste below.</>}
          </div>
        </div>
        {data?.configured && <button data-testid="btn-zoho-sync-now" onClick={syncNow} disabled={syncing} className="text-sm px-3 py-1.5 rounded bg-sky-600 text-white flex items-center gap-1 disabled:opacity-50"><RefreshCw size={12} className={syncing?"animate-spin":""}/>Sync now</button>}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-[11px] uppercase font-semibold text-slate-500">Client ID {data?.client_id_masked && <span className="text-slate-400 normal-case">(saved: {data.client_id_masked})</span>}</label><input data-testid="zoho-client-id" value={f.client_id||""} onChange={e=>setF({...f, client_id:e.target.value})} placeholder="1000.XXXXXX..." className="zi"/></div>
          <div><label className="text-[11px] uppercase font-semibold text-slate-500">Client Secret</label><input data-testid="zoho-client-secret" type="password" value={f.client_secret||""} onChange={e=>setF({...f, client_secret:e.target.value})} placeholder="Leave blank to keep existing" className="zi"/></div>
          <div className="col-span-2"><label className="text-[11px] uppercase font-semibold text-slate-500">Refresh Token</label><input data-testid="zoho-refresh-token" type="password" value={f.refresh_token||""} onChange={e=>setF({...f, refresh_token:e.target.value})} placeholder="1000.abcdef... — Leave blank to keep existing" className="zi"/></div>
          <div><label className="text-[11px] uppercase font-semibold text-slate-500">Organization ID *</label><input data-testid="zoho-org-id" value={f.organization_id||""} onChange={e=>setF({...f, organization_id:e.target.value})} placeholder="From Zoho Books URL" className="zi"/></div>
          <div><label className="text-[11px] uppercase font-semibold text-slate-500">Region</label><select data-testid="zoho-region" value={f.region||"in"} onChange={e=>setF({...f, region:e.target.value})} className="zi"><option value="in">.in (India)</option><option value="com">.com (US)</option><option value="eu">.eu (EU)</option><option value="com.au">.com.au</option></select></div>
        </div>
        <div className="flex gap-4 pt-2 border-t border-slate-100">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.sync_invoices??true} onChange={e=>setF({...f, sync_invoices:e.target.checked})}/>Sync Invoices</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.sync_purchase_orders??true} onChange={e=>setF({...f, sync_purchase_orders:e.target.checked})}/>Sync Purchase Orders</label>
        </div>
        <div className="flex justify-end">
          <button data-testid="btn-zoho-save" onClick={save} className="text-sm px-4 py-1.5 rounded bg-sky-600 text-white">Save</button>
        </div>
        <div className="text-[11px] text-slate-500">Runs automatically every hour via platform cron. Duplicate detection by invoice_no / ppo_no — already imported items are skipped.</div>
      </div>
      <style>{`.zi{width:100%;font-size:.85rem;border:1px solid #E2E8F0;border-radius:6px;padding:.4rem .5rem;background:#fff}`}</style>
    </div>
  );
}
